/** biome-ignore-all lint/style/useNamingConvention: external data (Optimizely tracking properties) */
'use client';

import { Suspense, useEffect } from 'react';
import { useIntersectionTracking } from '@/app_lib/hooks/useIntersectionTracking';
import { useUser } from '@/app_lib/providers/UserProvider';
import { GoogleTagManager } from '@/app_lib/services/Gtm';
import type { PageViewDataLayerObject } from '@/app_lib/types/Gtm';
import type { PianoPageConfig } from '@/app_lib/types/piano/Piano';
import { cn } from '@/app_lib/utils/utils';

/**
 * ConferencesWrapper component sets up client-sided page-level configurations.
 *
 * @param children - React children nodes to be rendered inside the wrapper.
 * @param pageConfig - Configuration object for Piano page settings.
 * @param url - The canonical URL of the article, used for tracking.
 *
 * @remarks
 * - Initializes Piano context configuration on mount.
 * - Tracks optimizely events.
 */
export const ConferencesWrapper = ({
  children,
  pianoConfig,
  url,
  dataLayer,
  className = '',
  ...rest
}: {
  children?: React.ReactNode;
  pianoConfig: PianoPageConfig;
  url: string;
  dataLayer: PageViewDataLayerObject;
  className?: string;
}) => {
  const { setPianoPageConfig } = useUser();

  useEffect(() => {
    setPianoPageConfig(pianoConfig);
  }, [setPianoPageConfig, pianoConfig]);

  useIntersectionTracking('past-events', { URL: url, value: 1 }, 'saw_past_conferences');
  useIntersectionTracking('upcoming-events', { URL: url, value: 1 }, 'saw_upcoming_conferences');
  useIntersectionTracking('no-events', { URL: url, value: 1 }, 'saw_no_filters');
  useIntersectionTracking('events-loader', { URL: url, value: 1 }, 'saw_events_loading');
  useIntersectionTracking('featured-event', { URL: url, value: 1 }, 'saw_featured_event');
  useIntersectionTracking('receive-updates', { URL: url, value: 1 }, 'saw_lead_gen_form');

  return (
    <div
      className={cn('conferences-page-wrapper flex flex-col', className)}
      {...rest}
    >
      <Suspense fallback={null}>
        <GoogleTagManager dataLayer={dataLayer} />
      </Suspense>
      {children}
    </div>
  );
};
